import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isToday } from 'date-fns/isToday';
import { useCallback, useMemo } from 'react';

import { validAccountNumber } from '@eika-infrastruktur/format';
import { useQueryClient } from '@eika-infrastruktur/react-query';

import { useConfig } from '../../../../defaultConfig';
import { ensureAccountsData, useAccounts } from '../../../../fetchHooks/useAccount';
import { ensureImmediatePaymentCreditorInfo } from '../../../../fetchHooks/useOcr';
import usePaymentLimits, { ensurePaymentLimitsData } from '../../../../fetchHooks/usePaymentLimits';
import type { Account, PaymentLimits } from '../../../../generated-types';
import useStepUp from '../../../../hooks/useStepUp';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../../services/accountFilters';
import TranslationsConfigType from '../../../../translations/translationType';
import { doesAmountExceedPaymentLimit, formatLimitAmountError } from '../../../../utils/validation';
import { canReceiverMakeImmediatePayment } from '../../../../utils/validation/receiver';
import { UseValidateHook } from '../../../../utils/validation/validateTypes';
import type { SelectedReceiver } from '../../SelectedReceiver';
import { invalidDate } from '../../validate';
import { validKid } from './kidService';
import { PaymentState } from './state';

export interface PaymentError {
    accountId?: string;
    amount?: string;
    amountLimit?: string;
    note?: string;
    toAccount?: string;
    paymentDate?: string;
    immediate?: string;
    kid?: string;
}

export const isNoCoverage = (state: PaymentState, accounts: Account[] | undefined): boolean => {
    const { fromAccount, amount, paymentDate, firstBankingDateFromToday } = state;
    const targetFromAccount = (accounts ?? []).find((account) => account.accountNumber.value === fromAccount?.value);
    const isBankDaysBetweenNowAndPaymentDate =
        (firstBankingDateFromToday && paymentDate && isBefore(firstBankingDateFromToday, paymentDate)) ||
        isEqual(paymentDate!, firstBankingDateFromToday!);

    if (!fromAccount || !targetFromAccount) return false;
    if (!amount) return false;
    if (isBankDaysBetweenNowAndPaymentDate && !!paymentDate && !isToday(paymentDate)) return false;

    const balance = targetFromAccount.localBalance?.availableBalance ?? targetFromAccount.balance.availableBalance;
    return balance < amount && !targetFromAccount.rights.overdraft;
};

export const amountLimitWarning = (
    translated: TranslationsConfigType,
    state: PaymentState,
    paymentLimits: PaymentLimits | undefined,
    accounts: Account[] | undefined,
): string | undefined => {
    const { amount, paymentDate, futurePaymentAmount, fromAccount } = state;
    const targetFromAccount = (accounts ?? []).find((account) => account.accountNumber.value === fromAccount?.value);
    const shouldValidateAgainsPaymentLimit =
        targetFromAccount?.owners.find((x) => x.primaryOwner)?.equalToUserLoggedOn ?? true;
    if (!shouldValidateAgainsPaymentLimit) return undefined;

    const paymentLimitError = doesAmountExceedPaymentLimit(amount, paymentDate, paymentLimits, futurePaymentAmount);
    return formatLimitAmountError(translated, paymentLimitError, paymentLimits) ?? undefined;
};

export const useAmountLimitWarning = (state: PaymentState) => {
    const translated = useTranslation();
    const { data: paymentLimits } = usePaymentLimits();
    const { data: accounts } = useAccounts();

    return useMemo(() => {
        return amountLimitWarning(translated, state, paymentLimits, accounts);
    }, [translated, state, paymentLimits, accounts]);
};
export const validateKid = (
    translated: TranslationsConfigType,
    kid: string | undefined,
    enableKidValidation: boolean,
    toAccount?: SelectedReceiver,
    immediate?: boolean,
) => {
    if (immediate || !enableKidValidation || !toAccount) return undefined;
    if (toAccount.kidRequired && (!kid || kid.length === 0)) {
        return translated.paymentValidation.missingKID;
    }
    if (!validKid(toAccount.kidFormats, kid)) {
        return translated.paymentValidation.invalidKID;
    }
    return undefined;
};

export const validateNote = (
    translated: TranslationsConfigType,
    note: string,
    toAccount?: SelectedReceiver,
    overwriteNoteRequired = false,
): string | undefined => {
    if (note && note.length && !note.replace(/\s/g, '').length) {
        return translated.invalidMessage;
    }
    if (!overwriteNoteRequired && toAccount?.noteRequired && note.replace(/\s/g, '') === '')
        return translated.paymentValidation.missingNote;
    return undefined;
};

export const validateNoteWithMaxLength = (
    translated: TranslationsConfigType,
    note: string,
    maxLength: number,
    toAccount: SelectedReceiver | undefined,
): string | undefined =>
    note.length > maxLength
        ? format(translated.tooManyCharacters, maxLength)
        : validateNote(translated, note, toAccount);

export const validateKidWithMaxLength = (
    translated: TranslationsConfigType,
    kid: string,
    maxLength: number,
    toAccount: SelectedReceiver | undefined,
    enableKidValidation: boolean,
) =>
    kid.length > maxLength
        ? format(translated.tooManyCharacters, maxLength)
        : validateKid(translated, kid, enableKidValidation, toAccount, false);

export const validate =
    (
        translated: TranslationsConfigType,
        paymentLimits: PaymentLimits | undefined,
        accounts: Account[],
        enableKidValidation: boolean = false,
        metadata: {
            blockedBanks?: string;
            canMakeImmediatePayment: boolean | undefined;
        } = { canMakeImmediatePayment: false },
    ) =>
    (formState: PaymentState): PaymentError => {
        const errors: PaymentError = {
            accountId: undefined,
            amount: undefined,
            amountLimit: undefined,
            note: undefined,
            toAccount: undefined,
            paymentDate: undefined,
            immediate: undefined,
            kid: undefined,
        };

        const { fromAccount, toAccount, amount, paymentDate, note, immediate, kid } = formState;

        const targetFromAccount = accounts
            .filter(isValidAccountPaymentFrom)
            .find((account) => account.accountNumber.value === fromAccount?.value);

        if (!fromAccount || !targetFromAccount?.accountId || targetFromAccount?.accountId.value === '') {
            errors.accountId = translated.paymentValidation.chooseFromAccount;
        }

        if (!toAccount) {
            errors.toAccount = translated.paymentValidation.chooseToAccount;
        }

        if (toAccount && !validAccountNumber(toAccount.accountNumber.value)) {
            errors.toAccount = format(translated.invalidToAccount, toAccount.accountNumber.value);
        }

        if (toAccount?.isOCR && immediate) {
            errors.immediate = translated.paymentValidation.receiverDontAcceptImmediatePayment;
        }

        if (
            immediate &&
            toAccount &&
            ((metadata.blockedBanks && !canReceiverMakeImmediatePayment(toAccount, metadata.blockedBanks)) ||
                !(metadata.canMakeImmediatePayment ?? true))
        ) {
            errors.immediate = translated.paymentValidation.receiverDoesNotSupportImmediatePayment;
        }

        if (!amount || amount <= 0) {
            errors.amount = translated.paymentValidation.chooseAmount;
        } else if (isNoCoverage(formState, accounts)) {
            errors.amount = translated.paymentValidation.noCoverage;
        } else {
            errors.amount = amountLimitWarning(translated, formState, paymentLimits, accounts);
        }

        if (invalidDate(paymentDate)) {
            errors.paymentDate = translated.paymentValidation.chooseValidDate;
        }

        if (kid?.value || toAccount?.kidRequired) {
            errors.kid = validateKid(translated, kid?.value, enableKidValidation, toAccount, immediate);
        } else {
            errors.note = validateNote(translated, note, toAccount);
        }

        return errors;
    };

export const useValidate: UseValidateHook<PaymentState, PaymentError> = () => {
    const queryClient = useQueryClient();
    const { featureToggles, blockedImmediateBanks } = useConfig();
    const translated = useTranslation();

    const { isHighAssurance } = useStepUp();
    const ensureImmediatePayment = ensureImmediatePaymentCreditorInfo(
        queryClient,
        featureToggles.enableImmediatePaymentCreditorCheck && isHighAssurance,
    );

    const paymentLimits = usePaymentLimits();
    const { data: accounts } = useAccounts();

    return useCallback(
        async (state) => {
            const currentPaymentLimits = await ensurePaymentLimitsData(queryClient, featureToggles.enablePaymentLimits);
            const currentAccounts = await ensureAccountsData(queryClient);
            const canMakeImmediatePayment = await ensureImmediatePayment(
                state.toAccount?.accountNumber,
                state.fromAccount,
                state.immediate,
            );
            const preparedValidate = validate(
                translated,
                paymentLimits.data ?? currentPaymentLimits,
                accounts ?? currentAccounts,
                featureToggles.enableKidValidation,
                { blockedBanks: blockedImmediateBanks, canMakeImmediatePayment: canMakeImmediatePayment?.available },
            );

            return preparedValidate(state);
        },
        [
            paymentLimits,
            queryClient,
            translated,
            accounts,
            featureToggles.enableKidValidation,
            featureToggles.enablePaymentLimits,
            blockedImmediateBanks,
            ensureImmediatePayment,
        ],
    );
};

export default useValidate;
